import { BarChart, Bar, Spinner } from "@rio-cloud/rio-uikit";
import { getOccurrenceStepLabelByStepValue, OccurrenceStepEnum } from "../../../../enums/occurrenceStep";
import { formatMilisecondsInDaysHoursMinutes } from "../../../../utils/dateUtils";
import { OccurrenceWithTimeSpent } from "../../../../models/occurrence";

export interface StepChartProps {
    adjustDataWithMinHeight: (data: any[], minHeightPercentage: number) => any[];
    data: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
    finalizedData?: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
    chartWidthMultiplier: number;
    defaultChartMinWidth: number;
    dynamicBarSize: number;
    isResponsiveView: boolean;
    loading?: boolean;
    metric?: "quantity" | "averageTime";
}

export const StepChart = (props: StepChartProps) => {
    const data = Object.values(OccurrenceStepEnum).map(key => {
        const qtd = props.data[key].filter(item => item.currentStep)?.length ?? 0;
        const qtdFinalized = props.finalizedData?.[key]?.length ?? 0;

        const averageTime = (props.data[key].filter(item => item.currentStep)?.reduce((acc, curr) => acc + (curr.steps[key]?.total || 0), 0) ?? 0) / qtd;
        const averageTimeFinalized = (props.finalizedData?.[key]?.reduce((acc, curr) => acc + (curr.steps[key]?.total || 0), 0) ?? 0) / (qtdFinalized || 1);

        const label = getOccurrenceStepLabelByStepValue(key);
        return {
            // Forçando o tamanho do label para 15 caracteres
            label: label.length > 15
                ? label.slice(0, 15) + "..."
                : label,
            qtd,
            qtdFinalized,
            averageTime,
            averageTimeFinalized,
        };
    });

    const CustomLabel = ({ x, y, width, index }: any) => {
        const label = props.metric === "quantity" ? data[index].qtd : formatMilisecondsInDaysHoursMinutes(data[index].averageTime * 60000);
        const hasFinalizedData = !!props.finalizedData;
        return (
            <text
                x={x + width / 2}
                y={y - 6}
                textAnchor="middle"
                fill="#a7afbb"
                fontSize={hasFinalizedData
                    ? (props.isResponsiveView ? "10px" : "12px")
                    : (props.isResponsiveView ? "12px" : "16px")
                }
            >
                {label}
            </text>
        );
    };

    const CustomLabelFinalized = ({ x, y, width, index }: any) => {
        const label = props.metric === "quantity" ? data[index].qtdFinalized : formatMilisecondsInDaysHoursMinutes(data[index].averageTimeFinalized * 60000);
        return (
            <text
                x={x + width / 2}
                y={y - 6}
                textAnchor="middle"
                fill="#a7afbb"
                fontSize={props.isResponsiveView ? "10px" : "12px"}
            >
                {label}
            </text>
        );
    };

    const hasCurrentData = Object.values(props.data).some(item => item.length > 0);
    const hasFinalizedData = !!props.finalizedData;
    
    return (
        <div
            style={{
                minWidth: "100%",
                width: `${props.chartWidthMultiplier * (data.length || 0)}px`,
                height: props.isResponsiveView ? '150px' : '185px',
            }}
            className="pb-2"
        >
            <div className="flex justify-center gap-6 mb-2">
                {hasFinalizedData && (
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm bg-coldplay-primrose" />
                        <span className="text-lg text-gray-600">
                            {props.metric === "quantity" ? "Finalizados" : "Tempo médio finalizados"}
                        </span>
                    </div>
                )}
                {hasCurrentData && (
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm bg-highlight" />
                        <span className="text-lg text-gray-600">
                            {props.metric === "quantity" ? "Em andamento" : "Tempo médio em andamento"}
                        </span>
                    </div>
                )}
            </div>

            {props.loading ? (
                <div className="flex justify-center items-center h-[150px]">
                    <Spinner show text="Carregando gráfico..." />
                </div>
            ) : (
                <BarChart
                    data={props.adjustDataWithMinHeight(data, 1)}
                    dataKey="label"
                    xAxisOptions={{
                        type: "category",
                        interval: props.isResponsiveView ? 1 : 0,
                        angle: -45,
                        textAnchor: "end",
                        height: 60,
                        tickMargin: 8,
                        padding: { left: 70, right: 30 },
                        tick: {
                            angle: props.isResponsiveView ? -45 : -25,
                            textAnchor: 'end',
                            dy: props.isResponsiveView ? 0 : 3
                        } as any
                    }}
                    tooltip={false}
                    showYAxis={false}
                    margin={{ bottom: 10, top: 31 }}
                    style={{
                        color: "#a7afbb",
                        fontSize: 12,
                        textAlign: "center",
                    }}
                    width={Math.max(
                        (data.length || 0) *
                        props.chartWidthMultiplier,
                        props.defaultChartMinWidth
                    )}
                    bars={[
                        ...(props.finalizedData ? [
                            <Bar
                                key="finalizedBar"
                                isAnimationActive={false}
                                dataKey={(e: any) => props.metric === "quantity" ? e.qtdFinalized : e.averageTimeFinalized}
                                radius={[3, 3, 0, 0]}
                                label={<CustomLabelFinalized />}
                                barSize={props.dynamicBarSize * (hasCurrentData ? 0.5 : 1)}
                                color="color-coldplay-primrose"
                            />
                        ] : []),
                        ...(hasCurrentData ? [
                            <Bar
                                key="bar"
                                isAnimationActive={false}
                                dataKey={(e: any) => props.metric === "quantity" ? e.qtd : e.averageTime}
                                radius={[3, 3, 0, 0]}
                                label={<CustomLabel />}
                                barSize={props.dynamicBarSize * (props.finalizedData ? 0.5 : 1)}
                                color="color-highlight"
                            />,
                        ] : []),
                    ]}
                />
            )}
        </div>
    );
};